import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, EyeIcon } from "@heroicons/react/24/outline";
import { Container, EmptyState, Loading, PageHeader, RowAction, Table, Tag } from "../../ui";
import { destinationOf, formatKigaliDate, useStaffScholarships, useStaffScholarship } from "../model";
import NomineesPanel from "./NomineesPanel";
import { TransmissionCard } from "./Transmission";

// The nominations reach the owner once HEC or the DG sends them; until the scholarship is decided the sponsor is still answering
const sentForApproval = (s) => Boolean(s.sentToDgAt);
const progressTag = (s) => (s.state === "DECIDED" ? <Tag color="green">Decided</Tag> : <Tag color="yellow">Nominations open</Tag>);

// The owner's (the sponsor's) scholarships with nominees to answer
export function PartnerScholarshipsPage() {
  const navigate = useNavigate();
  const { data, isPending, isError, refetch } = useStaffScholarships({ size: 100 });
  const scholarships = (data?.content ?? []).filter(sentForApproval);
  const open = (s) => navigate(`/partner/${s.id}`);

  const columns = [
    { key: "name", label: "Scholarship", render: (s) => <span className="font-medium text-slate-800">{s.name}</span> },
    { key: "academicYear", label: "Academic year", accessor: (s) => s.academicYear?.name ?? "—" },
    { key: "country", label: "Country", accessor: (s) => destinationOf(s) },
    { key: "sentToDgAt", label: "Nominations received", accessor: (s) => formatKigaliDate(s.sentToDgAt), sortValue: (s) => s.sentToDgAt },
    { key: "progress", label: "Nominations", accessor: (s) => (s.state === "DECIDED" ? "Decided" : "Open"), render: progressTag },
    { key: "open", label: "", exportable: false, render: (s) => <RowAction Icon={EyeIcon} label="Open" onClick={() => open(s)} /> },
  ];

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Scholarships" subtitle="Mark each of HEC's nominees granted or not granted." />
      {isError ? (
        <EmptyState title="Could not load your scholarships" onRefresh={refetch} />
      ) : (
        <Table cardView
          columns={columns}
          data={scholarships}
          onRowClick={open}
          isLoading={isPending}
          searchable={false}
          exportName="partner-scholarships"
          emptyTitle="No nominees yet. They appear here once HEC sends its nominations."
        />
      )}
    </Container>
  );
}

// One scholarship's transmission from HEC and its nominees, for the owner to answer; admissions are HEC's to record
export function PartnerScholarshipPage() {
  const { scholarshipId } = useParams();
  const { data, isPending, isError, refetch } = useStaffScholarship(scholarshipId);
  const scholarship = data?.data;

  if (isPending) return <Loading label="Loading the scholarship…" />;
  if (isError || !sentForApproval(scholarship))
    return <EmptyState title="Could not load this scholarship" description="Its nominations may not be sent yet, or you may not have access." onRefresh={refetch} />;

  return (
    <Container className="flex flex-col gap-6 py-6">
      <Link to="/partner" className="flex w-fit items-center gap-1 text-sm font-medium text-brand hover:underline">
        <ArrowLeftIcon className="h-4 w-4" /> All scholarships
      </Link>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {scholarship.name}
            {progressTag(scholarship)}
          </span>
        }
        subtitle={`${destinationOf(scholarship)} · nominations received ${formatKigaliDate(scholarship.sentToDgAt)}`}
      />
      <TransmissionCard scholarship={scholarship} />
      <NomineesPanel scholarship={scholarship} isOwner basePath="staff" />
    </Container>
  );
}
